/* eslint-disable react/no-danger */
/**
 * GovdataBar
 * 
 * @Author: Focci
 * @Date: 2023-08-09 14:52:18
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-09 14:52:18
 */

import PropTypes from 'prop-types'
import AddIcon from '@mui/icons-material/Add'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import NorthIcon from '@mui/icons-material/North'
import SouthIcon from '@mui/icons-material/South'
import SVGDollar from '@img/dollar-circle.svg'
import SVGLandarea from '@img/land-area.svg'
import clsx from 'clsx'
import { formatLang } from '@lib/datetime'
import { isValid } from '@lib/utils'
import { useLocale, useTranslations } from 'next-intl'

function GovdataItem({
  showGrowValue,
  Icon = NorthIcon,
  ExtroIcon = null,
  className = 'bg-primary',
  iconClassName = '',
  priceClassName = 'text-f1.2 font-medium',
  title = '',
  value = '',
  meta = '',
}) {
  return (
    <div 
      className={clsx([
        'p-4 flex items-center justify-center relative',
        className
      ])}
    >
      <div className="flex items-center">
        {showGrowValue && (
          <div className={iconClassName}>
            <Icon sx={{ fontSize: '2rem' }} />
          </div>
        )}
        <div className="flex flex-col ml-1">
          <span>{title}</span>
          <span className={priceClassName}>
            {value}
          </span>
          {showGrowValue && (
            <span className="opacity-70">{meta}</span>
          )}
        </div>
      </div>
      {ExtroIcon && (
        <div 
          className="
            absolute z-1 
            md:-right-6 md:top-1/2 md:-translate-y-1/2
            -bottom-4 md:bottom-auto 
            left-1/2 md:left-auto 
            -translate-x-1/2 md:-translate-x-0
          "
        >
          <div 
            className="
              w-8 md:w-12 h-8 md:h-12 bg-white rounded-full text-primary
              flex items-center justify-center shadow-simple
            "
          >
            <ExtroIcon sx={{ fontSize: '1.8rem' }} />
          </div>
        </div>
      )}
    </div>
  )
}

function GovdataBarAuItem({ icon, title, value, growth, meta, bordered }) {
  return (
    <div className={clsx(['flex items-center px-6 gap-2 w-1/2', bordered && 'md:border-l'])}>
      <div className="w-12 h-12 text-white bg-[#33a4f8] flex items-center justify-center rounded-full shrink-0">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-meta">{title}</span>
        <b className="text-f1.3" dangerouslySetInnerHTML={{ __html: value }} />
        {meta}
      </div>
      {growth}
    </div>
  )
}

function GovdataBarAu({ data, growth, meta }) {
  const th = useTranslations('house')
  const Icon = growth ? NorthIcon : SouthIcon

  return (
    <div className="bg-[#e7f5fe] flex flex-col py-4 gap-4 md:flex-row items-start md:items-center">
      <GovdataBarAuItem 
        icon={<SVGDollar width="1.3rem" height="1.3rem" />}
        title={th('landValue')}
        value={data.land_value}
        meta={data.land_value_change && <span>{meta}</span>}
        growth={
          data.land_value_change && (
            <div className={clsx([growth ? 'text-[#FF3838]' : 'text-[#0fb007]'])}>
              <Icon className="size-6" />
            </div>
          )
        }
      />
      {data.land_area && (
        <GovdataBarAuItem
          bordered
          icon={<SVGLandarea width="1.3rem" height="1.3rem" />}
          title={th('landArea')}
          value={data.land_area}
        />
      )}
    </div>
  )
}

export default function GovdataBar({
  data = {}
}) {
  const { 
    improvement_value: iv,
    land_value: lv,
    rateable_value: rv,
    improvement_value_change: vivc,
    rateable_value_change: vrvc,
    land_value_change: vlvc,
  } = data || {}
  
  const locale = useLocale()
  const th = useTranslations('house')
  const validIvc = isValid(vivc)
  const validRvc = isValid(vrvc)
  const validLvc = isValid(vlvc)

  const rvc = parseFloat(vrvc) > 0
  const ivc = parseFloat(vivc) > 0
  const lvc = parseFloat(vlvc) > 0

  const lvMeta = formatLang(
    data.last_valuation_at,
    th(
      lvc ? 'improvementValueOfYear' : 'descendValueOfYear', 
      { value: vlvc }
    ),
    locale
  )

  // 澳洲来的数据， 只有土地价值
  if(lv && !iv && !rv) {
    return (
      <GovdataBarAu data={data} growth={lvc} meta={lvMeta} />
    )
  }
  
  return iv && lv && rv && (
    <div className="grid grid-cols-1 md:grid-cols-3">
      <GovdataItem
        showGrowValue={validIvc}
        className="bg-[#99d0ff]"
        iconClassName={ivc ? 'text-[#FF3838]' : 'text-[#0fb007]'}
        title={th('buildingValue')}
        value={iv}
        Icon={ivc ? NorthIcon : SouthIcon}
        ExtroIcon={AddIcon}
        meta={formatLang(
          data.last_valuation_at,
          th(
            ivc ? 'improvementValueOfYear' : 'descendValueOfYear', 
            { value: vivc }
          ),
          locale
        )}
      />
      <GovdataItem
        showGrowValue={validLvc}
        className="bg-[#c3e3ff]"
        iconClassName={lvc ? 'text-[#FF3838]' : 'text-[#0fb007]'}
        title={th('landValue')}
        value={lv}
        Icon={lvc ? NorthIcon : SouthIcon}
        ExtroIcon={DragHandleIcon}
        meta={lvMeta}
      />
      <GovdataItem
        showGrowValue={validRvc}
        className="bg-primary text-white" 
        title={data.valuation_at ? `${th('govcv')}(${formatLang(data.valuation_at, th('yearMonth'))})` : th('govcv')}
        value={rv}
        Icon={rvc ? NorthIcon : SouthIcon}
        meta={formatLang(
          data.last_valuation_at,
          th(
            rvc ? 'improvementValueOfYear' : 'descendValueOfYear', 
            { value: vrvc }
          ),
          locale
        )}
      />
    </div>
  )
}

GovdataBar.propTypes = {
  data: PropTypes.object,
}
